/**
 * VendorApp.jsx — Iter 50.  Sidebar shell for Services + Credit vendors.
 * Mirrors AgencyApp layout (sidebar + main canvas).
 *
 * Sidebar: Engagements (service requests) · Catalog · Documents · Settings · Sign out.
 *
 * The vendor's `vendor_type` drives the accent (emerald for services, amber
 * for credit) and icon.  All sub-screens share the live `vendor` object
 * fetched once + a reload helper.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import api from '@/api';
import { Buildings, Bank, ClipboardText, FileText, Gear, SignOut, Storefront, Warning } from '@phosphor-icons/react';
import VendorHome from './vendor/VendorHome';
import VendorRequests from './vendor/VendorRequests';
import VendorCatalog from './vendor/VendorCatalog';
import VendorDocuments from './vendor/VendorDocuments';
import VendorSettings from './vendor/VendorSettings';

const VendorApp = ({ user, setUser }) => {
  const location = useLocation();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const r = await api.get('/vendor/me');
      setVendor(r.data.vendor);
    } catch (e) {
      if (e.response?.status === 404) setVendor(null);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const logout = () => {
    // (Iter 52) Portal-specific logout redirect. Use a hard browser navigation
    // (window.location.assign) because a soft navigate() races with App.js's
    // /vendor/* fallback <Navigate to="/services/login"/> during the same
    // React commit — the fallback wins even when navigate() runs first. See
    // /app/test_reports/iteration_48.json for the full RCA.
    const loginPath = vendor?.vendor_type === 'credit' ? '/credit/login' : '/services/login';
    localStorage.removeItem('token'); localStorage.removeItem('user');
    window.location.assign(loginPath);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-text-muted">Loading…</div>;

  if (!vendor) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-2xl p-8 max-w-md text-center" data-testid="vendor-no-profile">
        <Warning size={28} className="mx-auto mb-3 text-warning" />
        <h2 className="text-xl font-heading font-bold mb-2">No vendor profile linked</h2>
        <p className="text-sm text-text-secondary mb-4">Your login email doesn&rsquo;t match any vendor application. If you just applied, check your inbox for the approval email with a magic link to set your password.</p>
        <button onClick={logout} className="px-4 py-2 rounded-lg text-sm font-semibold bg-text-primary text-background" data-testid="vendor-no-profile-logout">Log out</button>
      </div>
    </div>
  );

  const isService = vendor.vendor_type === 'service';
  const accent = isService ? '#10b981' : '#f59e0b';
  const Icon = isService ? Buildings : Bank;
  const typeLabel = isService ? 'Startup Services' : 'Startup Credit';

  const NAV = [
    // (Iter 65) Guided service requests — founders who picked this vendor for a
    // specific product and are waiting on a final quote. Services only; credit
    // vendors have no equivalent flow.
    ...(isService ? [{ path: '/vendor/requests', label: 'Engagements', icon: ClipboardText, testid: 'vendor-nav-requests' }] : []),
    { path: '/vendor/catalog',    label: 'Catalog',     icon: Storefront, testid: 'vendor-nav-catalog' },
    { path: '/vendor/documents',  label: 'Documents',   icon: FileText,   testid: 'vendor-nav-documents' },
    { path: '/vendor/settings',   label: 'Settings',    icon: Gear,       testid: 'vendor-nav-settings' },
  ];

  return (
    <div className="flex min-h-screen bg-background text-text-primary" data-testid="vendor-app">
      <aside className="w-56 border-r border-border flex flex-col">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}>
            <Icon size={16} weight="fill" className="text-white" />
          </span>
          <div>
            <div className="text-[9px] uppercase tracking-[0.24em] font-bold" style={{ color: accent }}>Founderboard</div>
            <div className="text-sm font-heading font-bold text-text-primary leading-none">{isService ? 'Services' : 'Credit'}</div>
          </div>
        </div>
        <div className="px-5 py-3 border-b border-border">
          <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Vendor</div>
          <div className="text-sm font-medium text-text-primary truncate" data-testid="vendor-shell-name">{vendor.name}</div>
          <div className="text-[10px] text-text-muted capitalize mt-0.5">{typeLabel} &middot; <span style={{ color: accent }}>{(vendor.status || '').replace(/_/g, ' ')}</span></div>
        </div>
        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {NAV.map((n) => {
            const I = n.icon;
            const active = location.pathname === n.path || (n.path !== '/vendor' && location.pathname.startsWith(n.path));
            return (
              <Link key={n.path} to={n.path} data-testid={n.testid}
                className={`flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${active ? 'bg-surface text-text-primary' : 'text-text-secondary hover:bg-surface/60 hover:text-text-primary'}`}>
                <I size={14} weight="bold" /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="px-2 pb-3">
          <button onClick={logout} data-testid="vendor-logout" className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-danger hover:bg-danger/10 rounded-lg">
            <SignOut size={14} weight="bold" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <Routes>
          <Route path="/" element={isService ? <Navigate to="/vendor/requests" replace /> : <VendorHome vendor={vendor} accent={accent} />} />
          {isService && <Route path="/requests" element={<VendorRequests accent={accent} />} />}
          <Route path="/catalog" element={<VendorCatalog vendor={vendor} accent={accent} onReload={reload} />} />
          <Route path="/documents" element={<VendorDocuments vendor={vendor} accent={accent} onReload={reload} />} />
          <Route path="/settings" element={<VendorSettings vendor={vendor} accent={accent} user={user} onReload={reload} />} />
          <Route path="*" element={<Navigate to="/vendor" replace />} />
        </Routes>
      </main>
    </div>
  );
};

export default VendorApp;
